
import { Injectable, OnDestroy } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, Observable, Subject, throwError } from "rxjs";
import { catchError, map } from 'rxjs/operators';
import { AuthProxy, CredentialsCache } from "../../../common/auth";
import * as cred from "../../../common/auth/services/service.auth.proxy";
import { LogManager } from "../../../common/log/services/log.manager";
import { LogCat } from "../../../common/log/services/log.manager.categories";
import { TranslateProxy } from "../../../common/translate/services/translate.proxy";
import { ThemeChangerProxy } from "../../../puzzlehead/components/theme.example/theme.changer/theme.changer.proxy";

@UntilDestroy()
@Injectable()
export class LoginService implements  OnDestroy{
	public loginWatcher:Subject<string>;

	private refreshTokenTimeoutID: NodeJS.Timeout; //Holds the previous refresh token timeout ID so that we can clear it when we create a new one

	constructor(private authProxy:AuthProxy, private credentialsContainer:CredentialsCache, private translateProxy:TranslateProxy,
				private themeChangerProxy: ThemeChangerProxy) {
		LogManager.debug(LogCat.AppShell.Login, "Auth Service spawned");
		// it may have been a better choice to go for AsyncSubject since it helps getting the user only
		// once logged it, and we dont support at this point relogin with no refresh, but it is better
		// to keep the option for later if needed
		this.loginWatcher = new BehaviorSubject<string>(null);
	}

	observeLogins(){
		return this.loginWatcher.asObservable();
	}

	safeExpiration(expiration:number){
		return Math.round(expiration/2);
	}

	setShsCredentials(shs):Observable<any> {//ng.IPromise<any>
		LogManager.debug(LogCat.AppShell.Login, "set credentials from shs");
		return this.authProxy.getTokenFromShs(shs).pipe(map((token)=>{
			this.credentialsContainer.token = token;
			this.loginWatcher.next("shs");
			if (token.refreshToken){
				this.setRefreshToken(token.refreshToken, this.safeExpiration(token.expiresIn));
			}
		}));

	}

	// notice - this only clears the login information in the current object.
	// you probably want to use the LogoutService and not call this directly1
	clearLoginInfo(){

		const sessionStorageToken: cred.IAccessToken  = this.credentialsContainer.getFromSessionStorage();

		if (sessionStorageToken && sessionStorageToken.refreshToken) {
			this.authProxy.invalidateRefreshToken(sessionStorageToken.refreshToken)
				.pipe(untilDestroyed(this)).subscribe((success) => {
				LogManager.debug(LogCat.AppShell.Login, "Successfully invalidated refresh token");
			}, (err) => {
				LogManager.error(LogCat.AppShell.Login, "Invalidate refresh token failed", err);
			});
			this.credentialsContainer.clearSessionStorage();
		}

		this.loginWatcher.next(null);
		this.credentialsContainer.token=null;
	}

	setCredentials(userName, password, external_activation):Observable<any> {//ng.IPromise<any>
		LogManager.debug(LogCat.AppShell.Login, "set credentials");
		return this.authProxy.getToken(userName, password, external_activation).pipe(map((token)=>{
			this.credentialsContainer.token = token;
			this.loginWatcher.next(userName);
			if (token.refreshToken){
				this.setRefreshToken(token.refreshToken, this.safeExpiration(token.expiresIn));
			}
		}), catchError((e)=>this.handleError(e)));
	}

	public verifyOTPCode(code: number, isInitializeTOTP: boolean, isResetTOTP: boolean, rememberThisDevice: boolean) {
		LogManager.debug(LogCat.AppShell.Login, "verify OTP code");
		return this.authProxy.MFAGetToken(code, isInitializeTOTP, isResetTOTP, rememberThisDevice).pipe(map((token)=>{
			this.credentialsContainer.MFAToken = null;
			this.credentialsContainer.token = token;
			this.loginWatcher.next("mfa");
			if (token.refreshToken) {
				this.setRefreshToken(token.refreshToken, this.safeExpiration(token.expiresIn));
			}
		}));
	}

	// refresh tokens not really used at this point (we may decide to use it later - currently the client
	// supports it, but the server does not return it
	public setRefreshToken(refreshToken:string,refreshInterval:number):void {
		LogManager.debug(LogCat.AppShell.Login,"refresh timer set for " + refreshInterval+" seconds");

		//Clear the previous timeout before creating a new one
		if (this.refreshTokenTimeoutID) {
			clearTimeout(this.refreshTokenTimeoutID);
		}
		this.refreshTokenTimeoutID = setTimeout(() => {
			LogManager.debug(LogCat.AppShell.Login, "refresh timer running");
			this.authProxy.refreshToken(refreshToken)
				.pipe(untilDestroyed(this))
				.subscribe(
					retrievedToken => {
						LogManager.debug(LogCat.AppShell.Login, "get token returned");
						this.credentialsContainer.token = retrievedToken;
						let newRefreshInterval = this.safeExpiration(retrievedToken.expiresIn);
						let newRefreshToken = retrievedToken.refreshToken||refreshToken;
						// set the next refresh
						this.setRefreshToken(newRefreshToken,newRefreshInterval);
					}, (badReply) => {
						LogManager.error(LogCat.AppShell.Login, "token not received");
						// if failed to get a new Token retry every 2 seconds
						this.setRefreshToken(refreshToken, 2);
					});
		}, refreshInterval*1000);
	}

	// returns current user default language key. This will allow setting the application direction before
	// app.session.module with Kendo rtl provider is initialized
	public getUserLanguage():Observable<any> {
		return this.translateProxy.getUserLanguage();
	}

	public getUserTheme():Observable<any> {
		return this.themeChangerProxy.getUserTheme();
	}

	public changePassword(userName: string, oldPassword: string, newPassword: string): Observable<any> {
		return this.authProxy.changePassword(userName, oldPassword, newPassword);
	}

	public handleServerException(ex: string): string {
		if (ex.length > 42) {
			let exSplit: string[] = ex.split(' ');
			let res = 'Error';
			for (let i = exSplit.length - 3; i < exSplit.length; i++) {
				res += this.capitalize(exSplit[i].trim().replace('.', ''));
			}
			return res;
		} else if (ex.indexOf(' ') > 0) {
			let exSplit: string[] = ex.split(' ');
			let res: string = '';
			exSplit.forEach(a => {
				res += this.capitalize(a.trim().replace('.', ''));
			});
			return res;
		}
		return ex;
	}

	public capitalize(a: string): string {
		let toCapitalize = a.substr(0, 1).toUpperCase();
		return toCapitalize + a.substr(1);
	}

	private handleError(e: any) {
		if (e.error.error && (e.error.error === "604" || e.error.error === "606")) { //MFA required or Initialize TOTP
			if (e.error.error_uri) {
				this.credentialsContainer.MFAToken = JSON.parse(e.error.error_uri);
			}
		}

		return throwError(e);
  	}

	ngOnDestroy(): void {
	}
}
